import PropTypes from 'prop-types';
import {Component} from 'react';
import {connect} from 'react-redux';
import mobilePageNumbers from '../../modules/mobile-step-buttons/MobilePageNumbers';
import language from '../lang/language';
import Translation from '../lang/Translation';
import {isMobile} from '../selectors/DeviceSelectors';
import bigQueryMetricRecorder from '../tracking/bigQueryMetricRecorder';
import format from '../utils/format';
import Track from '../utils/tracking';
import containsLuhnCheckedNumber from './field-validation/containsLuhnCheckedNumber';
import xss from './field-validation/xss';

export class Field2 extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.onBlur = this.onBlur.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onChange = this.onChange.bind(this);
    this.validate = this.validate.bind(this);
    this._validate = this._validate.bind(this);
    
    this.state = {
      errorText: undefined,
      errorCode: [],
    };
    
    this.registered = false;
  }
  
  componentDidMount() {
    if (!this.registered && !this.props.disabled) {
      this.props.fieldContainer.registerField(this);
      this.registered = true;
    }
  }
  
  componentDidUpdate() {
    if (this.registered && this.props.disabled) {
      this.props.fieldContainer.deregisterField(this);
      this.registered = false;
    } else if (!this.registered && !this.props.disabled) {
      this.props.fieldContainer.registerField(this);
      this.registered = true;
    }
  }
  
  componentWillUnmount() {
    this.props.fieldContainer.deregisterField(this);
  }
  
  _normalize(event) {
    const {target} = event;
    if (this.props.definedField.type === 'checkbox') {
      return target.checked;
    }
    if (this.props.definedField.normalize) {
      let start = target.selectionStart;
      let end = target.selectionEnd;
      const useLength = (end === target.value.length && start === target.value.length);
      const normalizedValue = this.props.definedField.normalize(target.value);
      if (useLength) {
        start = target.value.length;
        end = start;
      }
      target.setSelectionRange(start, end);
      if (target.value === normalizedValue) {
        return normalizedValue;
      }
      target.value = normalizedValue;
    }
    
    if (this.props.definedField.filtersAndFormat) {
      return this.props.definedField.filtersAndFormat.reduce((filteredValue, filter) => filter(target.value), target.value);
    }
    
    return target.value;
  }
  
  validate() {
    const validationResult = this._validate(this.props.value);
    return {
      isValid: validationResult.isValid,
      errors: (validationResult.errorCode.length > 0) ? validationResult.errorCode[0] : undefined,
    };
  }
  
  getErrors(validations, value) {
    validations = validations || [];
    const {addressType} = this.props;
    return validations.reduce((errors, validation) => {
      const error = validation(value);
      if (error) {
        if (Array.isArray(error)) {
          if (language.doesTranslationExist(`${error[0]}.${addressType}`)) {
            errors.text.push(format(Translation.getText(`${error[0]}.${addressType}`), error.splice(1)));
            errors.errorCode.push(`${error[0]}.${addressType}`);
          } else {
            errors.text.push(format(Translation.getText(error[0]), error.splice(1)));
            errors.errorCode.push(error[0]);
          }
        } else {
          errors.text.push(Translation.getText(error));
          errors.errorCode.push(error);
        }
      }
      return errors;
    }, {text: [], errorCode: []});
  }
  
  _validate(value, shouldRecord) {
    const definedValidate = this.props.definedField.validate;
    const valueToValidate = this.props.definedField.validationCleaner
      ? this.props.definedField.validationCleaner(value) : value;
    
    let validate;
    if (!definedValidate) {
      validate = [];
    } else if (typeof definedValidate === 'function') {
      validate = [definedValidate];
    } else {
      validate = [...definedValidate];
    }
    
    if (this.props.definedField.mobileStepIgnoreValidation // Ability to ignore address validation step
      && this.props.isOnMobile && this.props.progressStep === mobilePageNumbers.ADDRESS_PAGE) {
      validate = validate.filter(validator => this.props.definedField.mobileStepIgnoreValidation.indexOf(validator) === -1);
    }
    
    if (!this.props.definedField.xssCheckExempt) {
      validate.push(xss);
    }
    
    const getValidatorName = (name) => {
      const addressFields = ['streetName', 'nameNumber', 'addressLine2'];
      return addressFields.includes(name) ? 'address' : name;
    };
    
    const fieldName = this.props.definedField.name;
    
    if (fieldName !== 'creditCardNumber' && fieldName !== 'residentID' && fieldName !== 'iban' && fieldName !== 'giftcardNumber' && fieldName !== 'diggecardGiftcardNumber') {
      validate.push(containsLuhnCheckedNumber(getValidatorName(fieldName)));
    }

    const errors = this.getErrors(validate, valueToValidate);

    const isValid = errors.text.length === 0;
    if (!isValid && shouldRecord && (this.state.errorCode[0] !== errors.errorCode[0])) {
      try {
        const error = {type: this.props.nameOnForm, label: errors.errorCode[0]};
        bigQueryMetricRecorder('inputErrorShown', [], [error], 'impression');
      } catch (error) {
        Track.track(error);
      }
    }
    
    const errorDetails = {
      errorText: errors.text[0],
      errorCode: errors.errorCode,
    };
    
    this.setState(errorDetails);
    
    return {
      isValid,
      ...errorDetails,
    };
  }
  
  setValue(value) {
    this.props.fieldContainer.setFieldValue(this.props.definedField.name, value);
  }
  
  onBlur(event, shouldValidate = true) {
    if (this.props.definedField.type === 'checkbox') {
      return true;
    }
    
    try {
      const fieldSelectedAttributesList = [{
        name: 'field',
        value: this.props.nameOnForm,
      }];
      
      bigQueryMetricRecorder('blurredInputField', fieldSelectedAttributesList);
    } catch (error) {
      Track.track(error);
    }
    
    if (this.props.definedField.transform) {
      const value = this.props.definedField.transform(event.target.value);
      if (value !== event.target.value) {
        event.target.value = value;
        this.setValue(value);
      }
    }
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
    
    if (shouldValidate) {
      this._validate(event.target.value, true);
    }
    return true;
  }
  
  onChange(event) {
    const value = this._normalize(event);
    this.setValue(value);
    if (this.props.onChange) {
      this.props.onChange(event);
    }
    if (this.props.definedField.type === 'checkbox') {
      this._validate(value);
    } else if (this.state.errorText) {
      this.setState({
        errorText: undefined,
        errorCode: [],
      });
    }
  }
  
  onFocus() {
    try {
      const fieldSelectedAttributesList = [{
        name: 'field',
        value: this.props.nameOnForm,
      }];
      
      bigQueryMetricRecorder('focusedInputField', fieldSelectedAttributesList);
    } catch (error) {
      Track.track(error);
    }
  }
  
  render() {
    return this.props.children(this.onChange, this.onBlur, this.onFocus, this.state.errorText);
  }
}

Field2.propTypes = {
  children: PropTypes.func.isRequired,
  fieldContainer: PropTypes.object.isRequired,
  definedField: PropTypes.object.isRequired,
  nameOnForm: PropTypes.string.isRequired,
  
  // Optional
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  type: PropTypes.string,
  filtersAndFormat: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.object]),
  disabled: PropTypes.bool,
  addressType: PropTypes.string,
};

Field2.defaultProps = {
  definedField: {},
};

const mapStateToProps = state => ({
  isOnMobile: isMobile(state),
  progressStep: state.ui.checkoutProgressStep,
});

export default connect(mapStateToProps)(Field2);
